import React, { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { SpaceIcon } from 'components/atoms/space-icon';
import { Page } from 'components/molecules/page';
import {
  useActiveProjectTemplate,
  useHasSetupSpacesInTemplate,
  useProjectTemplateSpaces,
} from 'data/templates/projects';
import { setupSpaceInProjectTemplateAction } from 'data/templates/projects/actions';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { SettingupPhaseItem } from './phase-item';

const PageContent = styled(Page.Content)`
  height: calc(100% - 76px);
`;

const Wrapper = styled.div`
  align-items: center;
  background: var(--coolest-gray-20);
  display: flex;
  height: 100%;
  padding: var(--layout-07);
`;

const Hexagon = styled(SpaceIcon)`
  --fill-color: var(--gray-100);
  height: 48px;
  width: 48px;
`;

const Content = styled.div`
  max-width: 400px;

  h5 {
    margin-bottom: var(--layout-02);
  }
`;

const List = styled.ol`
  list-style: none;
  padding: 0;

  li {
    align-items: center;
    display: flex;
    margin-bottom: var(--spacing-03);

    span {
      margin-left: var(--spacing-04);
    }
  }
`;

const TIMER = 1900;

export const SettingUpProjectPlanIntermediateScreen: FC = () => {
  const pageTitle = useTranslation('templates.setting_up.title');
  const spaces = useProjectTemplateSpaces();
  const spaceIds = Object.keys(spaces);
  const spaceId = spaceIds[0];
  const template = useActiveProjectTemplate();
  const history = useHistory();
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const spaceSetupState = useHasSetupSpacesInTemplate();
  const currentUser = useUser();

  useEffect(() => {
    dispatch(
      setupSpaceInProjectTemplateAction({
        companyName: currentUser.company.companyName,
        handleFailure() {
          history.replace(`/templates/projects/${template.templateId}`);
        },
      }),
    );
  }, [dispatch, currentUser.company.companyName, history, template.templateId]);

  useEffect(() => {
    if (spaceSetupState === 'COMPLETE') {
      history.push(`${url}/${spaceId}`);
    }
  }, [history, spaceSetupState, spaceId, url]);

  return (
    <Page>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Page.Header
        title={<Page.Header.Title>{template.templateName}</Page.Header.Title>}
      />
      <PageContent>
        <Wrapper>
          <Content>
            <Hexagon />
            <h5>
              <FormattedMessage id="templates.setting_up" />
            </h5>
            <List>
              {Object.values(spaces).map((space, index) => (
                <SettingupPhaseItem
                  key={space.spaceId}
                  title={space.spaceName}
                  timeUntilLoading={(index + 1) * TIMER}
                />
              ))}
            </List>
          </Content>
        </Wrapper>
      </PageContent>
    </Page>
  );
};
